// components/gallery/gallery.js
import React, { useMemo, useState } from 'react';
import Moment from 'moment';
import ReactPlayer from 'react-player';

import FeatureItem from './galleryFeaturedItem';
import GalleryItem from './galleryItem';
import {
  getCurrentExperience,
  visibilityAllowsExperience,
} from '../../utils/visibility';

/**
 * Gallery (page-level) component with filters.
 *
 * Props:
 *  - gallery: object (galleryPages[0]) expected shape:
 *      { galleryGroups: [{ name, description, galleryItems: [...] }, ...], ... }
 *
 * Behavior:
 *  - Apply visibility filter (visibilityValue) first (internal/seasonal)
 *  - Include all visible items, including documents alongside videos
 *  - Build group / year / month filter controls from the visible dataset
 *  - Only show groups that have visible items
 *  - Hide entire section if no visible items at all
 */

function getExperience() {
  return getCurrentExperience();
}

function isVisibleByExperience(item) {
  const experience = getExperience(); // 'internal' or 'seasonal'
  return visibilityAllowsExperience(item && item.visibilityValue, experience);
}

// Utility to detect videos (fallback to file.url extension)
function isVideo(item) {
  if (!item) return false;
  const url = item?.file?.url || '';
  if (item.featuredVideo) return true;
  if (!url) return false;
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return true;
  return ReactPlayer.canPlay(url);
}

export default function Gallery({ gallery }) {
  const groups = useMemo(() => (gallery && Array.isArray(gallery.galleryGroups) ? gallery.galleryGroups : []), [gallery]);

  const visibleAllGroups = useMemo(() => {
    return groups
      .map((g) => {
        const items = (g.galleryItems || []).filter((it) => !!it && isVisibleByExperience(it));
        return { name: g.name, description: g.description, items };
      })
      .filter((g) => g.items.length > 0);
  }, [groups]);

  const visibleGroups = visibleAllGroups;

  // Flatten visible items for featured and for building filter options
  const visibleItems = useMemo(() => {
    return visibleGroups.reduce((acc, g) => acc.concat(g.items.map((it) => ({ ...it, groupName: g.name }))), []);
  }, [visibleGroups]);

  // Build filter option lists based on visibleItems only
  const groupsForSelect = useMemo(() => {
    const names = [...new Set(visibleItems.map((i) => i.groupName).filter(Boolean))];
    return names;
  }, [visibleItems]);

  // UI state
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  // When year changes, reset month
  const onYearChange = (ev) => {
    setSelectedYear(ev.target.value);
    setSelectedMonth('');
  };

  // Compute filtered items after applying group/year/month on top of visibility
  const filteredItemsByGroup = useMemo(() => {
    const result = visibleGroups
      .map((g) => {
        let items = g.items.slice();

        if (selectedGroup) {
          if (g.name !== selectedGroup) items = [];
        }

        if (selectedYear) {
          items = items.filter((it) => {
            if (!it.releaseDate) return false;
            return Moment.utc(it.releaseDate).year() === parseInt(selectedYear, 10);
          });
        }

        if (selectedMonth) {
          items = items.filter((it) => {
            if (!it.releaseDate) return false;
            return Moment.utc(it.releaseDate).month() === parseInt(selectedMonth, 10);
          });
        }

        return { ...g, items };
      })
      .filter((g) => g.items.length > 0);

    return result;
  }, [visibleGroups, selectedGroup, selectedYear, selectedMonth]);

  // Featured videos follow the same filters as the item list.
  const featured = useMemo(() => {
    const videos = filteredItemsByGroup.reduce((acc, g) => acc.concat(g.items), []).filter(isVideo);
    const flagged = videos.filter((item) => item.featuredVideo);
    return (flagged.length ? flagged : videos.slice(0, 1))
      .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
  }, [filteredItemsByGroup]);

  // Build year options for the selected group (so month/year options are relevant)
  const yearsForSelected = useMemo(() => {
    const itemsForContext = selectedGroup
      ? visibleItems.filter((i) => i.groupName === selectedGroup)
      : visibleItems;
    const years = [
      ...new Set(itemsForContext.map((it) => (it.releaseDate ? Moment.utc(it.releaseDate).year() : null)).filter(Boolean)),
    ].sort((a, b) => b - a);
    return years;
  }, [visibleItems, selectedGroup]);

  const monthsForSelected = useMemo(() => {
    if (!selectedYear) return [];
    const itemsForContext = selectedGroup
      ? visibleItems.filter((i) => i.groupName === selectedGroup && Moment.utc(i.releaseDate).year() === parseInt(selectedYear, 10))
      : visibleItems.filter((i) => Moment.utc(i.releaseDate).year() === parseInt(selectedYear, 10));
    const months = [...new Set(itemsForContext.map((it) => (it.releaseDate ? Moment.utc(it.releaseDate).month() : null)).filter((m) => m !== null))].sort(
      (a, b) => a - b
    );
    return months;
  }, [visibleItems, selectedGroup, selectedYear]);

  if (!gallery || visibleItems.length === 0) return null;

  return (
    <section className="section">
      <div className="container-fluid">
        {/* Header + Filters */}
        {gallery.route === 'video-series' && (
          <header className="section-header row">
            <div className="col">
              <h2 className="section-title">Video Series</h2>
            </div>
          </header>
        )}

        {/* Filters row */}
        {!gallery.hideFilters && (
          <div className="row" style={{ marginBottom: 12 }}>
            <div className="col md-3">
              <label htmlFor="gallery-group-filter" className="assistive-text">Group</label>
              <select id="gallery-group-filter" value={selectedGroup} onChange={(e) => {
                setSelectedGroup(e.target.value);
                setSelectedYear('');
                setSelectedMonth('');
              }}>
                <option value="">{gallery.route === 'video-series' ? 'All Series' : 'All Groups'}</option>
                {groupsForSelect.map((gName, idx) => (
                  <option key={idx} value={gName}>
                    {gName}
                  </option>
                ))}
              </select>
            </div>

            <div className="col md-3">
              <label htmlFor="gallery-year-filter" className="assistive-text">Year</label>
              <select id="gallery-year-filter" value={selectedYear} onChange={onYearChange}>
                <option value="">All Years</option>
                {yearsForSelected.map((y, idx) => (
                  <option key={idx} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="col md-3">
              <label htmlFor="gallery-month-filter" className="assistive-text">Month</label>
              <select id="gallery-month-filter" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} disabled={!selectedYear}>
                <option value="">All Months</option>
                {monthsForSelected.map((m) => (
                  <option key={m} value={m}>
                    {Moment.months()[m]}
                  </option>
                ))}
              </select>
            </div>

            <div className="col md-3">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedGroup('');
                  setSelectedYear('');
                  setSelectedMonth('');
                }}
                type="button"
              >
                Clear filters
              </button>
            </div>
          </div>

        )}

        {filteredItemsByGroup.length === 0 && <p>No items match the selected criteria.</p>}

        {/* Featured videos */}
        {featured.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <FeatureItem featureItem={featured} />
          </div>
        )}

        {/* Groups / Items */}
        {filteredItemsByGroup.map((g, gi) => (
          <div key={gi} className="gallery-group" style={{ marginBottom: 32 }}>
            {g.name && (
              <header className="section-header row">
                <div className="col">
                  <h3 className="section-title">{g.name}</h3>
                  {g.description && <p className="section-description">{g.description}</p>}
                </div>
              </header>
            )}

            {/* GalleryItem component expects gallery shape. We'll pass items + contextual props */}
            <GalleryItem gallery={{ items: g.items }} />
          </div>
        ))}
      </div>
    </section>
  );
}
